import { assert } from './errors';

import type { EchoNavigation, EchoProps, EchoView } from '../types';

export async function parseView(response: Response): Promise<EchoView> {
  const view: EchoView = { type: 'view', props: {}, slots: {}, status: response.status };
  const root = document.createElement('template');

  // nosemgrep: insecure-document-method, insecure-innerhtml
  // eslint-disable-next-line security/no-inner-html
  root.innerHTML = await response.text();

  const scripts = root.content.querySelectorAll('script');
  assert(scripts.length <= 1, '<script> must not appear more than once');

  Array.from(root.content.children).forEach(node => {
    if (node instanceof HTMLScriptElement) {
      assert(node.type === 'application/json', '<script> has an incorrect content type');

      view.props = JSON.parse(node.textContent!) as EchoProps;
    } else if (node instanceof HTMLTemplateElement) {
      assert(node.id && !view.slots[node.id], '<template> must have a unique id');
      assert(!node.content.querySelector('script'), '<template> must not include inline scripts');

      view.slots[node.id] = node.content;
    } else {
      throw new Error('Echo response contains invalid top-level elements');
    }
  });

  return view;
}

export function parseNavigation(response: Response): Omit<EchoNavigation, 'replace'> | null {
  const location = response.headers.get('X-Echo-Redirect');
  if (!location) return null;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const reload = response.headers.get('X-Echo-Reload') === 'true';

  const url = new URL(location);
  const isSameOrigin = url.origin === window.location.origin;

  assert(response.status === 204, 'X-Echo-Redirect requires a 204 status code');
  assert(isSameOrigin, 'X-Echo-Redirect must not redirect to an external location');

  return { type: 'navigation', location, reload };
}

export function submitSearchParams(url: URL, formData: FormData): EchoNavigation {
  const params = new URLSearchParams();

  Array.from(formData.entries()).forEach(([key, value]) => {
    assert(!(value instanceof File), 'Echo submission form data keys cannot be files');

    params.append(key, value);
  });

  const location = new URL(url);
  location.search = params.toString();

  return { type: 'navigation', location: location.toString(), replace: false, reload: false };
}
